import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import Select from 'react-select';
import api from '../services/api';
import { toast } from 'react-toastify';
import { formatMoney } from '../utils/currency';
import PromptDialog from '../components/common/PromptDialog';
import LoadingSpinner from '../components/common/LoadingSpinner';
import StatusBadge from '../components/common/StatusBadge';
import { useAuth } from '../context/AuthContext';
import { canAny } from '../hooks/usePermissions';
import { exportToCsv } from '../utils/exportCsv';

const DriverDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { admin } = useAuth();
  const canManagePayouts = canAny(admin, ['manage_payouts', 'manage_payments']);
  const [driver, setDriver] = useState(null);
  const [rides, setRides] = useState([]);
  const [currencyCode, setCurrencyCode] = useState('QAR');
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [savingCategory, setSavingCategory] = useState(false);
  const [loading, setLoading] = useState(true);
  const [ledgerDialogOpen, setLedgerDialogOpen] = useState(false);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [payoutDialogOpen, setPayoutDialogOpen] = useState(false);
  const [payoutLoading, setPayoutLoading] = useState(false);
  const [earningsRangeOpen, setEarningsRangeOpen] = useState(false);
  const [earningsDownloading, setEarningsDownloading] = useState(false);

  useEffect(() => {
    fetchDriverDetails();
    fetchCategoryOptions();
  }, [id]);

  const fetchDriverDetails = async () => {
    try {
      const response = await api.get(`/admin/drivers/${id}`);
      const nextDriver = response.data.data.driver;
      setDriver(nextDriver);
      setRides(response.data.data.recentRides);
      setCurrencyCode(String(response.data?.currencyCode || response.data?.data?.currencyCode || 'QAR').toUpperCase());
      if (nextDriver?.vehicle?.vehicleType) {
        setSelectedCategory({
          value: nextDriver.vehicle.vehicleType,
          label: nextDriver.vehicle.vehicleType,
        });
      }
    } catch (error) {
      console.error('Failed to fetch driver details');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategoryOptions = async () => {
    try {
      const response = await api.get('/admin/ride-types');
      const options = (response?.data?.data?.rideTypes || [])
        .filter((type) => type?.key)
        .map((type) => ({ value: type.key, label: type.label || type.key }));
      setCategoryOptions(options);
    } catch (error) {
      toast.error('Failed to load ride categories');
    }
  };

  const handleApprove = async () => {
    try {
      await api.put(`/admin/drivers/${id}/approve`);
      toast.success('Driver approved!');
      fetchDriverDetails();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to approve driver');
    }
  };

  const handleVerifyDocument = async (documentType, verified) => {
    try {
      await api.put(`/admin/drivers/${id}/verify-document`, { documentType, verified });
      toast.success(verified ? 'Document verified' : 'Document marked as pending');
      fetchDriverDetails();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update document verification');
    }
  };

  const handleDeleteDriver = async () => {
    const fullName = `${driver?.firstName || ''} ${driver?.lastName || ''}`.trim() || 'this driver';
    const confirmed = window.confirm(`Delete driver account for ${fullName}? This cannot be undone.`);
    if (!confirmed) return;

    try {
      await api.delete(`/admin/drivers/${id}`);
      toast.success('Driver deleted successfully');
      navigate('/drivers');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete driver');
    }
  };

  const handleLedgerAdjust = () => {
    setLedgerDialogOpen(true);
  };

  const handlePayoutConfirm = async ({ amount, bankReference, date, note }) => {
    const parsedAmount = Number(amount);
    if (!parsedAmount || parsedAmount <= 0) {
      toast.error('Amount must be a positive number');
      return;
    }
    if (!bankReference || !String(bankReference).trim()) {
      toast.error('Bank reference is required');
      return;
    }
    if (parsedAmount > (driver?.ledgerBalance || 0)) {
      toast.error(`Amount exceeds available balance (${formatMoney(driver.ledgerBalance, currencyCode)})`);
      return;
    }
    setPayoutLoading(true);
    try {
      await api.post(`/admin/drivers/${id}/payouts`, {
        amount: parsedAmount,
        bankReference: String(bankReference).trim(),
        date: date || undefined,
        note: note || undefined,
      });
      toast.success('Payout recorded successfully');
      setPayoutDialogOpen(false);
      fetchDriverDetails();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to record payout');
    } finally {
      setPayoutLoading(false);
    }
  };

  const handleDownloadEarnings = async (from, to) => {
    setEarningsDownloading(true);
    try {
      const params = { driverId: id };
      if (from) params.from = from;
      if (to) params.to = to;
      const stamp = new Date().toISOString().slice(0, 10);
      await exportToCsv('/admin/exports/driver-earnings', params, `driver-earnings-${id}-${stamp}.csv`);
      setEarningsRangeOpen(false);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to download earnings sheet');
    } finally {
      setEarningsDownloading(false);
    }
  };

  const handleLedgerConfirm = async ({ direction, amount, reason }) => {
    const directionClean = String(direction || '').trim().toLowerCase();
    if (!directionClean || !['in', 'out'].includes(directionClean)) {
      toast.error('Direction must be "in" or "out"');
      return;
    }
    if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) {
      toast.error('Amount must be a positive number');
      return;
    }
    setLedgerLoading(true);
    try {
      await api.post(`/admin/drivers/${id}/ledger-adjust`, {
        direction: directionClean,
        amount: Number(amount),
        reason: reason || undefined,
      });
      toast.success('Ledger balance adjusted');
      setLedgerDialogOpen(false);
      fetchDriverDetails();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to adjust ledger');
    } finally {
      setLedgerLoading(false);
    }
  };

  const handleUpdateCategory = async () => {
    if (!selectedCategory?.value) {
      toast.error('Please select a category');
      return;
    }

    if (selectedCategory.value === driver?.vehicle?.vehicleType) {
      toast.info('Category is already assigned');
      return;
    }

    setSavingCategory(true);
    try {
      const response = await api.put(`/admin/drivers/${id}/vehicle-type`, {
        vehicleType: selectedCategory.value,
      });
      setDriver(response.data.data);
      toast.success('Driver category updated');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update category');
    } finally {
      setSavingCategory(false);
    }
  };

  const documents = [
    {
      key: 'driverLicense',
      label: 'Driver License',
      numberLabel: 'Number',
      number: driver?.documents?.driverLicense?.number,
      imageUrl: driver?.documents?.driverLicense?.imageUrl,
      verified: Boolean(driver?.documents?.driverLicense?.verified),
    },
    {
      key: 'vehicleRegistration',
      label: 'Car License',
      numberLabel: 'Number',
      number: driver?.documents?.vehicleRegistration?.number,
      imageUrl: driver?.documents?.vehicleRegistration?.imageUrl,
      verified: Boolean(driver?.documents?.vehicleRegistration?.verified),
    },
    {
      key: 'insurance',
      label: 'Insurance',
      numberLabel: 'Policy',
      number: driver?.documents?.insurance?.policyNumber,
      imageUrl: driver?.documents?.insurance?.imageUrl,
      verified: Boolean(driver?.documents?.insurance?.verified),
    },
    {
      key: 'idCard',
      label: 'ID Card',
      numberLabel: 'Number',
      number: driver?.documents?.idCard?.number,
      imageUrl: driver?.documents?.idCard?.imageUrl,
      verified: Boolean(driver?.documents?.idCard?.verified),
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (!driver) {
    return <div className="text-center py-12">Driver not found</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to="/drivers" className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeftIcon className="w-5 h-5" />
          </Link>
          <h1 className="text-2xl font-bold text-gray-800">Driver Details</h1>
        </div>
        <div className="flex items-center gap-3">
          {driver.status === 'pending' && (
            <button onClick={handleApprove} className="btn-primary flex items-center">
              <CheckCircleIcon className="w-5 h-5 mr-2" />
              Approve Driver
            </button>
          )}
          <button onClick={handleDeleteDriver} className="btn-secondary text-red-600 border-red-200 hover:bg-red-50">
            Delete Driver
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Driver Info Card */}
        <div className="card">
          <div className="text-center">
            <div className="relative inline-block">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-green-600 font-bold text-2xl">
                  {driver.firstName?.[0]}{driver.lastName?.[0]}
                </span>
              </div>
              {driver.isOnline && (
                <span className="absolute bottom-4 right-0 w-4 h-4 bg-green-500 border-2 border-white rounded-full" />
              )}
            </div>
            <h2 className="text-xl font-bold">{driver.firstName} {driver.lastName}</h2>
            {driver.employeeNumber && (
              <p className="text-sm font-medium text-gray-400">ID #{driver.employeeNumber}</p>
            )}
            <p className="text-gray-500">{driver.email}</p>
            <div className="mt-2">
              <StatusBadge status={driver.emailVerified ? 'active' : 'pending'} label={driver.emailVerified ? 'Email Verified' : 'Email Not Verified'} />
            </div>
            <p className="text-gray-500">{driver.phone}</p>
            <div className="mt-3">
              <StatusBadge status={driver.status} />
            </div>
          </div>
        </div>

        {/* Vehicle Info */}
        <div className="card">
          <h3 className="text-lg font-semibold mb-4">Vehicle Information</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-500">Make</span>
              <span className="font-medium">{driver.vehicle?.make}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Model</span>
              <span className="font-medium">{driver.vehicle?.model}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Year</span>
              <span className="font-medium">{driver.vehicle?.year}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Color</span>
              <span className="font-medium">{driver.vehicle?.color}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">License Plate</span>
              <span className="font-medium">{driver.vehicle?.licensePlate}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Type</span>
              <span className="font-medium capitalize">{driver.vehicle?.vehicleType}</span>
            </div>
            <div className="pt-2 space-y-2">
              <span className="text-gray-500 text-sm">Assign Category</span>
              <Select
                value={selectedCategory}
                onChange={setSelectedCategory}
                options={categoryOptions}
                placeholder="Select category"
              />
              <button
                onClick={handleUpdateCategory}
                disabled={savingCategory}
                className="btn-primary w-full disabled:opacity-50"
              >
                {savingCategory ? 'Saving...' : 'Save Category'}
              </button>
            </div>
          </div>
        </div>

        {/* Stats Card */}
        <div className="card">
          <h3 className="text-lg font-semibold mb-4">Statistics</h3>
          <div className="space-y-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-indigo-600">{driver.totalRides || 0}</p>
              <p className="text-sm text-gray-500">Total Rides</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-yellow-500 text-center">
                ★ {(driver.ratingAverage ?? driver.rating)?.toFixed(1) ?? '—'}
              </p>
              <p className="text-sm text-gray-500 text-center">
                Rating{driver.ratingCount > 0 ? ` · ${driver.ratingCount} reviews` : ''}
              </p>
              {driver.ratingCount > 0 && driver.ratingBreakdown && (
                <div className="mt-3 space-y-1">
                  {[5, 4, 3, 2, 1].map((s) => {
                    const cnt = driver.ratingBreakdown[s] ?? driver.ratingBreakdown[String(s)] ?? 0;
                    const pct = Math.round((cnt / driver.ratingCount) * 100);
                    return (
                      <div key={s} className="flex items-center gap-1.5 text-xs">
                        <span className="w-5 text-right text-gray-500">{s}★</span>
                        <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                          <div className="bg-yellow-400 h-1.5 rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="w-4 text-right text-gray-400">{cnt}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-green-600">{formatMoney(driver.totalEarnings || 0, rides?.[0]?.fare?.currency || currencyCode || 'QAR')}</p>
              <p className="text-sm text-gray-500">Total Earnings</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-indigo-500">{formatMoney(driver.ledgerBalance || 0, currencyCode || 'QAR')}</p>
              <p className="text-sm text-gray-500">Ledger Balance</p>
            </div>
            <div className="pt-2 space-y-2">
              <button
                onClick={handleLedgerAdjust}
                className="btn-secondary w-full"
              >
                Adjust Ledger Balance
              </button>
              {canManagePayouts && (
                <>
                  <button
                    onClick={() => setPayoutDialogOpen(true)}
                    className="btn-primary w-full"
                    disabled={!driver?.ledgerBalance || driver.ledgerBalance <= 0}
                  >
                    Record Payout
                  </button>
                  <button
                    onClick={() => handleDownloadEarnings()}
                    disabled={earningsDownloading}
                    className="btn-secondary w-full"
                  >
                    {earningsDownloading ? 'Downloading…' : 'Download Earnings Sheet'}
                  </button>
                  <button
                    onClick={() => setEarningsRangeOpen(true)}
                    className="btn-secondary w-full text-xs"
                  >
                    Custom Date Range…
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Documents */}
      <div className="card">
        <h3 className="text-lg font-semibold mb-4">Documents</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {documents.map((doc) => (
            <div key={doc.key} className="p-4 border rounded-lg space-y-3">
              <div className="flex justify-between items-center">
                <span className="font-medium">{doc.label}</span>
                <StatusBadge status={doc.verified ? 'active' : 'pending'} label={doc.verified ? 'Verified' : 'Pending'} />
              </div>

              {doc.numberLabel && (
                <p className="text-sm text-gray-500">
                  {doc.numberLabel}: {doc.number || 'Not provided'}
                </p>
              )}

              {doc.imageUrl ? (
                <a
                  href={doc.imageUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-indigo-600 hover:text-indigo-800"
                >
                  View document image
                </a>
              ) : (
                <p className="text-sm text-red-600">Document not uploaded</p>
              )}

              <button
                className={doc.verified ? 'btn-secondary w-full' : 'btn-primary w-full'}
                disabled={!doc.imageUrl}
                onClick={() => handleVerifyDocument(doc.key, !doc.verified)}
              >
                {doc.verified ? 'Mark Pending' : 'Verify Document'}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Rides */}
      <div className="card">
        <h3 className="text-lg font-semibold mb-4">Recent Rides</h3>
        {rides.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="table-header">Date</th>
                  <th className="table-header">User</th>
                  <th className="table-header">Route</th>
                  <th className="table-header">Fare</th>
                  <th className="table-header">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rides.map((ride) => (
                  <tr key={ride._id}>
                    <td className="table-cell">{new Date(ride.createdAt).toLocaleDateString()}</td>
                    <td className="table-cell">{ride.user?.firstName} {ride.user?.lastName}</td>
                    <td className="table-cell max-w-xs truncate">{ride.pickup?.address}</td>
                    <td className="table-cell">{formatMoney(ride.fare?.totalFare || 0, ride.fare?.currency || currencyCode || 'QAR')}</td>
                    <td className="table-cell">
                      <StatusBadge status={ride.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">No rides yet</p>
        )}
      </div>
      <PromptDialog
        isOpen={ledgerDialogOpen}
        title="Adjust Ledger Balance"
        fields={[
          { name: 'direction', label: 'Direction', type: 'text', placeholder: '"in" or "out"', required: true },
          { name: 'amount', label: 'Amount (QAR)', type: 'number', placeholder: '0.00', required: true },
          { name: 'reason', label: 'Reason', type: 'text', placeholder: 'Optional reason' },
        ]}
        confirmLabel="Apply Adjustment"
        loading={ledgerLoading}
        onConfirm={handleLedgerConfirm}
        onCancel={() => setLedgerDialogOpen(false)}
      />
      <PromptDialog
        isOpen={payoutDialogOpen}
        title={`Record Payout — Balance: ${formatMoney(driver?.ledgerBalance || 0, currencyCode)}`}
        fields={[
          { name: 'amount', label: `Amount (${currencyCode})`, type: 'number', placeholder: String(driver?.ledgerBalance || ''), required: true },
          { name: 'bankReference', label: 'Bank Reference / Transfer ID', type: 'text', placeholder: 'e.g. TXN-20240615-001', required: true },
          { name: 'date', label: 'Transfer Date (optional)', type: 'text', placeholder: 'YYYY-MM-DD' },
          { name: 'note', label: 'Note (optional)', type: 'text', placeholder: 'Payroll cycle, batch ref, etc.' },
        ]}
        confirmLabel="Record Payout"
        loading={payoutLoading}
        onConfirm={handlePayoutConfirm}
        onCancel={() => setPayoutDialogOpen(false)}
      />
      <PromptDialog
        isOpen={earningsRangeOpen}
        title="Download Earnings Sheet — Custom Range"
        fields={[
          { name: 'from', label: 'From Date', type: 'text', placeholder: 'YYYY-MM-DD', required: true },
          { name: 'to', label: 'To Date', type: 'text', placeholder: 'YYYY-MM-DD', required: true },
        ]}
        confirmLabel={earningsDownloading ? 'Downloading…' : 'Download CSV'}
        loading={earningsDownloading}
        onConfirm={({ from, to }) => handleDownloadEarnings(from, to)}
        onCancel={() => setEarningsRangeOpen(false)}
      />
    </div>
  );
};

export default DriverDetails;
